import React, { useState } from 'react';
import './TableOfContents.css';

interface NavigationItem {
  id: string;
  title: string;
  level: number;
  children?: NavigationItem[];
  filename?: string;
}

interface TableOfContentsProps {
  navigation: NavigationItem[];
  currentSectionId: string;
  onSectionClick: (sectionId: string) => void;
}

interface TOCItemProps {
  item: NavigationItem;
  currentSectionId: string;
  onSectionClick: (sectionId: string) => void;
  level: number;
}

const TOCItem: React.FC<TOCItemProps> = ({ item, currentSectionId, onSectionClick, level }) => {
  const [isExpanded, setIsExpanded] = useState(level <= 2); // Auto-expand chapters and sections
  const hasChildren = item.children && item.children.length > 0;
  const isSelected = currentSectionId === item.id;
  const isClickable = item.filename; // Only items with filenames are clickable

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleClick = () => {
    if (isClickable) {
      onSectionClick(item.id);
    }
  };

  const getIndentLevel = () => {
    return Math.max(0, level - 1) * 20; // 20px per level, starting from level 1
  };

  return (
    <div className="toc-item">
      <div 
        className={`toc-item-header ${isSelected ? 'selected' : ''} ${isClickable ? 'clickable' : ''} level-${level}`}
        style={{ paddingLeft: `${getIndentLevel()}px` }}
        onClick={handleClick}
      >
        {hasChildren && (
          <button 
            className={`toc-expand-button ${isExpanded ? 'expanded' : ''}`}
            onClick={handleToggle}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <span className="toc-item-title">{item.title}</span>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="toc-children">
          {item.children!.map((child) => (
            <TOCItem
              key={child.id}
              item={child}
              currentSectionId={currentSectionId}
              onSectionClick={onSectionClick}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TableOfContents: React.FC<TableOfContentsProps> = ({ 
  navigation, 
  currentSectionId, 
  onSectionClick 
}) => {
  return (
    <div className="table-of-contents">
      <div className="toc-header">
        <h3>Table of Contents</h3>
      </div>
      <div className="toc-content">
        {navigation.map((item) => (
          <TOCItem
            key={item.id}
            item={item}
            currentSectionId={currentSectionId}
            onSectionClick={onSectionClick}
            level={1}
          />
        ))}
      </div>
    </div>
  );
};

export default TableOfContents;
